import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFrames } from '@/hooks/useFrames';
import { useFrameCategories } from '@/hooks/useFrameCategories';
import { BatchFrameUploadZone } from '@/components/frames/BatchFrameUploadZone';
import { FrameCard } from '@/components/frames/FrameCard';
import { api } from '@/lib/api';
import type { UploadFrameRequest } from '@photobooth/types';

export function FramesPage() {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const { frames, isLoading, error, refetch } = useFrames(selectedCategoryId || undefined);
  const { frameCategories } = useFrameCategories();

  const handleBatchUploadFile = async (
    file: File,
    metadata: UploadFrameRequest
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.frames.upload(file, metadata);
      if (response.success) return { success: true };
      return { success: false, error: response.error?.message ?? 'Upload failed' };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Upload failed' };
    }
  };

  const handleDelete = async (frameId: string) => {
    try {
      await api.frames.delete(frameId);
      refetch();
    } catch (err) {
      alert('Failed to delete frame');
    }
  };

  const getCategoryName = (frameCategoryId: string) => {
    return frameCategories.find((c) => c.frameCategoryId === frameCategoryId)?.name;
  };

  const filteredFrames = selectedCategoryId
    ? frames.filter((f) => f.frameCategoryId === selectedCategoryId)
    : frames;

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-text">Frames</h1>
          <p className="text-text-muted mt-1">
            Upload and manage photo frames for your booth
          </p>
        </div>
        <Link
          to="/frame-categories"
          className="text-primary hover:text-blue-700 text-sm font-medium"
        >
          Manage Frame Categories →
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <BatchFrameUploadZone
            onUploadComplete={refetch}
            frameCategories={frameCategories}
            uploadFile={handleBatchUploadFile}
          />
        </div>

        <div className="lg:col-span-2">
          <div className="bg-surface rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-text">Frame Library</h2>
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Categories</option>
                {frameCategories.map((cat) => (
                  <option key={cat.frameCategoryId} value={cat.frameCategoryId}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            ) : filteredFrames.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-2">🖼️</div>
                <p className="text-text-muted">No frames found</p>
                <p className="text-sm text-text-muted mt-1">
                  Upload your first frame to get started
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {filteredFrames.map((frame) => (
                  <FrameCard
                    key={frame.frameId}
                    frame={frame}
                    categoryName={getCategoryName(frame.frameCategoryId)}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
